import { PATH_DICTS_NETWORKS } from "@/constants";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { FilterType } from "@/lib/filters";
import { fetchNetworkList } from "@/services/network";
import { ReadNetwork } from "@/types/network";
import { ComponentProps, forwardRef, useCallback } from "react";
import { ControllerRenderProps } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AsyncSelect, { Option } from "./AsyncSelect";
import appConfiguration from "./cfg/AppCfg";
import { useCreateDataTableOptions } from "./data-table/hooks/useCreateDataTableOptions";
import { EntityListLink } from "./EntityListLink";

interface NetworkSelectProps extends ControllerRenderProps {
  className?: string;
  defaultItem?: Partial<Option>;
}

export default forwardRef<HTMLButtonElement, NetworkSelectProps>(
  function NetworkSelect({ value, defaultItem, ...props }, ref) {
    const { t } = useTranslation();
    const options = useCreateDataTableOptions("networkList");
    const queryKey = useDynamicQueryParams<ReadNetwork>("networkList", options);

    const onFilter: ComponentProps<typeof AsyncSelect>["onChange"] =
      useCallback(
        (filterValue) => {
          options.setFilterValue("name", {
            value: filterValue,
            filter: FilterType.IncludesString,
          });
        },
        [options],
      );

    return (
      <span className="flex items-center gap-1">
        <AsyncSelect
          queryKey={queryKey}
          queryFn={() =>
            fetchNetworkList({
              filters: options.filters,
              limit: appConfiguration.selectComponentsPaginationLimit,
            })
          }
          placeholder={t("networkSelect.placeholder")}
          loadingLabel={t("networkSelect.loadingNetworks")}
          noResultsLabel={t("networkSelect.noResults")}
          searchLabel={t("networkSelect.search")}
          enableFilter
          {...props}
          onFilter={onFilter}
          value={value ? value.toString() : undefined}
          ref={ref}
          defaultItem={defaultItem}
        />
        <EntityListLink
          path={PATH_DICTS_NETWORKS}
          label={t("networkSelect.link")}
        />
      </span>
    );
  },
);
